import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_TRIALDOI_LOAD_BY_TRIAL_ID,
    ACTION_TRIALDOI_LOAD_BY_ID,
    ACTION_TRIALDOI_LOAD_FAILURE,
    ACTION_TRIALDOI_LOAD_SUCCESS,

    ACTION_TRIALDOI_SAVE,
    ACTION_TRIALDOI_SAVE_SUCCESS,
    ACTION_TRIALDOI_SAVE_FAILURE,

    ACTION_TRIALDOI_VALIDATE,
    ACTION_TRIALDOI_VALIDATE_SUCCESS,
    ACTION_TRIALDOI_VALIDATE_FAILURE,

    ACTION_TRIALDOI_CREATE,
    ACTION_TRIALDOI_CREATE_SUCCESS,
    ACTION_TRIALDOI_CREATE_FAILURE,

    ACTION_TRIALDOI_SYNC,
    ACTION_TRIALDOI_SYNC_SUCCESS,
    ACTION_TRIALDOI_SYNC_FAILURE,

    ACTION_TRIALDOI_CLEAR,
    ACTION_TRIALDOI_FILTER,

    TrialDoiAction
} from '../actions/trialDoi';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState, IFormFilter } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";

export interface ITrialDoiState {
    formData: Dtos.TrialDoi | Dtos.TrialDoi[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[],
    loadState: IRequestState;
    saveState: IRequestState;
    validateState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
    formFilter: IFormFilter;
    formFilteredData: Dtos.TrialDoi[];
    formLength: number;
}

const initialState: ITrialDoiState = {
    formData: undefined,
    formState: undefined,
    formProperties: undefined,
    validationFailures: undefined,
    lookups: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    validateState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState,
    formFilter: {
        search: "",
        showInActive: false
    },
    formFilteredData: [],
    formLength: 0
};


function loading(state: ITrialDoiState, _isLoading: boolean): ITrialDoiState {
    const newState: ITrialDoiState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(
    state: ITrialDoiState,
    formData: Dtos.TrialDoi | Dtos.TrialDoi[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): ITrialDoiState {
    const newState: ITrialDoiState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return filter(newState, state.formFilter);
}

function loadFailure(state: ITrialDoiState, responseStatus: Dtos.ResponseStatus): ITrialDoiState {
    const newState: ITrialDoiState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

function filter(state: ITrialDoiState, formFilter: IFormFilter): ITrialDoiState {

    const items: Dtos.TrialDoi[] | undefined =
        state.formData instanceof Array ?
            state.formData :
            undefined;

    const formLength = items ? items.length : 0;

    const formFilteredData = items ? items.filter(p => (p.active == true || formFilter.showInActive) &&
        (
            formFilter.search == "" ||
            p.doi.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1
        )) : undefined;

    const newState: ITrialDoiState = update(state, {
        formFilter: {
            $set: formFilter
        },
        formFilteredData: {
            $set: formFilteredData
        },
        formLength: {
            $set: formLength
        }
    });

    return newState;
}

function clear(state: ITrialDoiState, propertiesToClear?: string[]): ITrialDoiState {

    if (!propertiesToClear) {
        const newState: ITrialDoiState = update(initialState, {});
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: ITrialDoiState, _isSaving: boolean): ITrialDoiState {
    const newState: ITrialDoiState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: ITrialDoiState,
    formData: Dtos.TrialDoi | Dtos.TrialDoi[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): ITrialDoiState {
    const newState: ITrialDoiState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: ITrialDoiState, responseStatus: Dtos.ResponseStatus): ITrialDoiState {
    const newState: ITrialDoiState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function validating(state: ITrialDoiState, _isValidating: boolean): ITrialDoiState {
    const newState: ITrialDoiState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function validateSuccess(
    state: ITrialDoiState,
    formData: Dtos.TrialDoi | Dtos.TrialDoi[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): ITrialDoiState {
    const newState: ITrialDoiState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        validateState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function validateFailure(state: ITrialDoiState, responseStatus: Dtos.ResponseStatus): ITrialDoiState {
    const newState: ITrialDoiState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: ITrialDoiState, _isCreating: boolean): ITrialDoiState {
    const newState: ITrialDoiState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: ITrialDoiState,
    formData: Dtos.TrialDoi,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[]
): ITrialDoiState {
    const newState: ITrialDoiState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: ITrialDoiState, responseStatus: Dtos.ResponseStatus): ITrialDoiState {
    const newState: ITrialDoiState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

const TrialDoiReducer: Reducer<ITrialDoiState> = (state: ITrialDoiState = initialState, action: TrialDoiAction) => {
    switch (action.type) {
        case ACTION_TRIALDOI_LOAD_BY_TRIAL_ID:
        case ACTION_TRIALDOI_LOAD_BY_ID:
            return loading(state, true);
        case ACTION_TRIALDOI_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_TRIALDOI_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_TRIALDOI_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_TRIALDOI_SAVE:
            return saving(state, true);
        case ACTION_TRIALDOI_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_TRIALDOI_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_TRIALDOI_VALIDATE:
            return validating(state, true);
        case ACTION_TRIALDOI_VALIDATE_SUCCESS:
            return validateSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_TRIALDOI_VALIDATE_FAILURE:
            return validateFailure(state, action.responseStatus);
        case ACTION_TRIALDOI_CREATE:
            return creating(state, true);
        case ACTION_TRIALDOI_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.lookups);
        case ACTION_TRIALDOI_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_TRIALDOI_FILTER:
            return filter(state, action.formFilter);
        case ACTION_TRIALDOI_SYNC:
            return loading(state, true);
        case ACTION_TRIALDOI_SYNC_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_TRIALDOI_SYNC_FAILURE:
            return loadFailure(state, action.responseStatus);

    }

    return state;
}

export default TrialDoiReducer;